import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const Upload = ({ kind, has, tenantId, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    if (tenantId) fd.append('tenant_id', tenantId);
    setBusy(true);
    try {
      await api.post('/branding/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${kind === 'logo' ? 'Logo' : 'Signature'} uploaded - it now appears on report cards & transcripts`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      {has && <img src={`${API}/branding/file/${kind}?tenant_id=${tenantId}&t=${Date.now()}`} alt={kind} data-testid={`branding-${kind}-preview`} className="h-10 max-w-[140px] object-contain border border-slate-100 rounded bg-white p-0.5" />}
      <button data-testid={`branding-${kind}-upload`} disabled={busy} onClick={() => ref.current?.click()}
        className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-orange-500">{busy ? 'Uploading...' : has ? `Replace ${kind}` : `Upload ${kind}`}</button>
      {has && <button data-testid={`branding-${kind}-remove`} onClick={async () => { await api.delete(`/branding/${kind}`, { params: tenantId ? { tenant_id: tenantId } : {} }); toast.success(`${kind} removed`); onDone(); }} className="text-xs text-rose-600 underline">Remove</button>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { if (e.target.files?.[0]) send(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
};

export const BrandingCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: b } = useQuery({
    queryKey: ['branding', tenantId],
    queryFn: () => api.get('/branding', { params: tenantId ? { tenant_id: tenantId } : {} }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const [names, setNames] = useState({ school_name: '', principal_name: '' });
  useEffect(() => { if (b) setNames({ school_name: b.school_name || '', principal_name: b.principal_name || '' }); }, [b]);
  const inval = () => qc.invalidateQueries({ queryKey: ['branding'] });
  const saveMut = useMutation({
    mutationFn: () => api.post('/branding/settings', { tenant_id: tenantId, ...names }),
    onSuccess: () => { toast.success('Branding names saved'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="branding-panel">
      <h2 className="font-display text-lg font-semibold mb-1">Report card branding</h2>
      <p className="text-sm text-slate-500 mb-4">Your logo, school name and principal's signature appear on exam report cards, transcripts and printed report cards.</p>
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="branding-school-name" placeholder="School name on documents" value={names.school_name} onChange={(e) => setNames({ ...names, school_name: e.target.value })} className="flex-1 min-w-[220px]" />
          <Input data-testid="branding-principal-name" placeholder="Principal's name" value={names.principal_name} onChange={(e) => setNames({ ...names, principal_name: e.target.value })} className="w-56" />
          <Button data-testid="branding-save-names" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>Save</Button>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1.5">School logo (PNG/JPEG, max 2MB)</p>
            <Upload kind="logo" has={b?.has_logo} tenantId={tenantId} onDone={inval} />
          </div>
          <div>
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1.5">Principal signature image</p>
            <Upload kind="signature" has={b?.has_signature} tenantId={tenantId} onDone={inval} />
          </div>
        </div>
      </div>
    </div>
  );
};
